import type { AppAnalyticsEvents } from "common/analytics/app-events";
import type { AppPlanCode } from "common/app-plans";
import type { PrimitiveGlideTypeKind } from "common/description";
import type { PageTemplateKind, SyncedDataSources } from "common/firebase-function-types";
import type { OrgWithNumEmployees } from "designer/apps/orgs/orgs-lib-types";
import type { EditedColumnKind } from "../store/tables-state";

type WithApp = { app_id: string };
type WithValue = { value: number };
type WithLabel = WithLabels<string>;
type WithLabels<T> = { label: T };

type WithDataSource = { data_source: "gsheet" | "glide" | SyncedDataSources };
type WithUserId = { user_id: string };
type WithOrgId = { org_id: string };
type WithColumnKind = { kind: EditedColumnKind | PrimitiveGlideTypeKind };

type WithAuthProvider = { provider: "email" | "google" };

// FIXME: Add app to most of these

export type AnalyticsEvents = AppAnalyticsEvents & {
    "picked sheet": {};
    "replaced sheet": {};
    "first publish for app": WithApp;

    "upgrade start": WithApp & { intent: string; plan: AppPlanCode };

    upgrade: WithApp & {
        plan: "basic" | "pro";
        period: "monthly" | "annual";
        privacy: "public" | "private";
        "private-users"?: number;
    };

    "upgrade failed": WithApp;

    downgrade: WithApp & {
        reason?: string;
        explanation?: string;
    };

    "copy sample": WithApp & {
        new_app_id?: string;
    };

    "copy glide template": WithApp & {
        template_appid: string;
        template_name: string;
        template_price: number;
        price_currency: string;
    };
    "buy template": WithApp & { price: number };
    "submit template": WithApp;

    "copy user template": WithApp;
    "sign up for copying user template": WithApp;

    "new app": {
        fromPagesAdvert?: boolean;
    };
    "new page": {};
    "new app created": WithDataSource &
        WithApp & {
            preferredFormFactor?: string;
            app_kind?: string;
            no_default_selection?: boolean;
            template?: PageTemplateKind;
        };

    "new project name and kind select": {
        app_kind?: string;
    };

    "new project name": {
        app_kind?: string;
    };

    "new project layout select": {
        app_kind?: string;
        page_template?: string;
    };

    "google sheet source select": {
        app_kind?: string;
    };

    "google sheet select": {
        app_kind?: string;
    };

    "glide data source select": {
        app_kind?: string;
    };

    "excel source select": {
        app_kind?: string;
    };

    "duplicate app": WithApp & { method: string; asPage?: boolean };
    "delete app": WithApp;

    "mobile open sample app": WithApp;

    config_group_changed: WithLabel;

    "help button": {};
    "help menu item": WithLabel;

    "tried pay button": WithLabel & WithApp;
    "used pay button": WithLabel & WithApp;
    "pay button failed": WithLabel & WithApp;

    "video progress": {
        // Video URL
        label: string;
        // Progress 0-100
        value: number;
    };

    tos_update_dialog: { status: "accept" | "reject" };

    "share app": WithLabel & WithApp;

    "sign in start": WithAuthProvider;
    "sign in success": WithAuthProvider;
    "sign in failed": { reason: string };
    "sign in link sent": {};

    "sign up started": WithAuthProvider;
    "sign up failed": { reason: string };
    "sign up success": WithUserId & WithAuthProvider & { referrer?: string };
    "sign up link sent": {};

    "sign up user survey": { intent: string };
    "sign up company survey": { name: string; industry: string; role: string };
    "sign up invite members": { count: number };
    "sign up accept tos": {};
    "completed sign up flow": { isForWork: boolean };

    "verified email": {};

    "sign in closed by user": {};
    "sign in blocked by popup blocker": {};
    "user modified app": WithApp & { app_rows?: number };

    builder_navigate: WithApp & WithLabel;
    builder_action:
        | (WithApp & WithLabels<"reload_sheet" | "edit_sheet" | "view_sheet">)
        | (WithApp & WithLabels<"undo" | "redo"> & { method: "keyboard" | "cursor" });

    // FIXME: Add `WithApp` to these, too
    set_platform_preview: WithLabel;
    set_preview_form_factor: WithLabel;
    set_preview_user: {};

    set_screen_layout: WithApp & WithLabel;

    component_new: WithApp & WithLabel;
    component_moved: WithApp;
    component_deleted: WithApp;

    special_value_new: WithApp & WithLabel;
    column_value_new: WithApp;

    set_theme: WithApp & WithLabel;
    set_theme_color: WithApp & WithLabel;

    set_icon: (WithApp & WithLabels<"emoji"> & { type: string }) | (WithApp & WithLabels<"file"> & WithValue);

    column_selected: WithApp & WithColumnKind;
    column_added: WithApp & WithColumnKind;
    column_changed: WithApp & WithColumnKind;
    column_canceled: WithApp & WithColumnKind;
    // FIXME: Add `WithColumnKind`
    column_deleted: WithApp;

    // FIXME: Add `WithApp` to these, too
    unsplash_image_picked: {};

    tab_new: WithApp;
    tab_moved: WithApp;
    tab_deleted: WithApp;
    tab_icon_changed: WithApp;

    // Orgs
    org_created: WithOrgId & OrgWithNumEmployees;
    org_member_invited: WithOrgId & { source: string };
    org_member_invite_accept: WithUserId & WithOrgId;

    "app invite sent": WithApp & WithUserId;
    "app invite accepted": WithApp & WithUserId;
    "app user invited": WithApp & WithLabel;

    "billing learn more": WithApp;

    // Builder templates
    "browse templates from builder": WithUserId;
    "copy builder template": WithUserId & { isProTrial: boolean; new_app_id?: string };
};

export const MarketingEvents: Array<keyof AnalyticsEvents> = [
    "first publish for app",

    "upgrade",

    "downgrade",
    "copy glide template",
    "copy sample",
    "buy template",
    "submit template",

    "sign up for copying user template",

    "new app",
    "new app created",
    "new project name and kind select",
    "new project name",
    "new project layout select",
    "google sheet source select",
    "google sheet select",
    "excel source select",
    "glide data source select",
    "sign in start",
    "sign in success",
    "sign in failed",

    "sign up started",
    "sign up failed",
    "sign up success",
    "sign up link sent",
    "completed sign up flow",

    "verified email",

    "app invite sent",
    "copy builder template",
];

// Important: All custom hubspot events must be created through the hubspot UI
// https://knowledge.hubspot.com/analytics-tools/create-custom-behavioral-events#use-custom-behavioral-events-in-workflows
// If you need to pass additonal properties, please make sure you also create custom properties for each event located on the event.
export const HubspotCustomEvents: Array<keyof AnalyticsEvents> = ["copy glide template"];
